/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-mixed-spaces-and-tabs */
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HelpIcon from "@mui/icons-material/Help";
import ErrorIcon from '@mui/icons-material/Error';
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import {
	CircularProgress,
	IconButton,
	InputAdornment,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Tooltip,	
	Link
} from "@mui/material";
import { useStore } from "react-redux";
import { AiSearchApiWrapper } from "lib/api/aiSearchApiWrapper";
import * as hooks from "lib/hooks";
import { Store } from "store";
import { AiSearchRequest, AiSearchResponse } from "models/dto/searchApi";
import React, { useState } from "react";

/** Allow only http/https or same-origin relative URLs for anchor href to avoid javascript:/data: etc. */
function isSafeActionUrl(url: string | null | undefined): boolean {
	if (url == null || typeof url !== "string") return false;
	const u = url.trim();
	if (u.length === 0) return false;
	const lower = u.toLowerCase();
	return lower.startsWith("https://") || lower.startsWith("http://") || (u.startsWith("/") && !u.startsWith("//"));
}

export function AiSearch() {
	const [inputValue, setInputValue] = useState("");
	const [response, setResponse] = useState<AiSearchResponse | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [feedbackOpen, setFeedbackOpen] = useState(false);
	const [feedbackRating, setFeedbackRating] = useState<1 | -1 | null>(null);
	const [feedbackComment, setFeedbackComment] = useState("");
	const [submittingFeedback, setSubmittingFeedback] = useState(false);
	const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
	const placeholder = "Search using natural language";

	const store = useStore() as Store;
	const { account } = hooks.UseAuth();
	const searchApi = AiSearchApiWrapper.GetInstance(store);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSearch();
		}
	};

	const handleSearch = async () => {
		if (!inputValue.trim() || isLoading) {
			return;
		}

		// Reset previous answer & feedback only when submitting a new query
		if (response) {
			setResponse(null);
			setFeedbackRating(null);
			setFeedbackComment("");
			setFeedbackSubmitted(false);
		} else {
			setResponse(null); // ensure cleared
		}

		setIsLoading(true);

		try {
			// Get user ID from account token claims, fallback to timestamp if not available
			let sessionId = "web-session-" + Date.now();
			account.IfSome((acc) => {
				const userIdentifier = acc.idTokenClaims?.gmuniqueidentifier;
				if (userIdentifier) {
					sessionId = String(userIdentifier);
				}
			});

			const searchRequest: AiSearchRequest = {
				question: inputValue.trim(),
				use_tool_calling: false,
				session_id: sessionId,
			};

			const result: AiSearchResponse =
				await searchApi.SearchQuery(searchRequest);
			setResponse(result);
		} catch (error: any) {
			if (error.message === "Network Error")
			{
				setResponse({
				type: "network_error",
				answer: null,
				sql: null,
				latency_ms: null,
				clarify: null,
				params: null,
				columns: null,
				rows: null,
				row_count: null,
				chart: null,
			});

			}
			else{
			setResponse({
				type: "text",
				answer: "Sorry, there was an error processing your request.",
				sql: null,
				latency_ms: null,
				clarify: null,
				params: null,
				columns: null,
				rows: null,
				row_count: null,
				chart: null,
			});
		}
		} finally {
			setIsLoading(false);
		}
	};

	const canFeedback = !!response?.message_id && !isLoading;

	const openFeedback = (rating: 1 | -1) => {
		if (!canFeedback) return;
		setFeedbackRating(rating);
		setFeedbackComment("");
		setFeedbackSubmitted(false);
		setFeedbackOpen(true);
	};

	const handleSubmitFeedback = async () => {
		if (!response?.message_id || !feedbackRating || submittingFeedback)
			return;
		let userId = "anonymous";
		account.IfSome((acc) => {
			const uid =
				acc.idTokenClaims?.gmuniqueidentifier || acc.localAccountId;
			if (uid) userId = String(uid);
		});
		setSubmittingFeedback(true);
		try {
			await searchApi.SendFeedback({
				message_id: response.message_id,
				rating: feedbackRating,
				comment: feedbackComment.trim() || undefined,
				user_id: userId,
			});
			setFeedbackSubmitted(true);
			// close after short delay
			setTimeout(() => setFeedbackOpen(false), 1200);
		} catch (e) {
			// keep dialog open; optionally we could show error state
		} finally {
			setSubmittingFeedback(false);
		}
	};

	const feedbackActions = (
		<Stack direction="row" spacing={1} alignItems="center">
			<IconButton
				disabled={!canFeedback || feedbackSubmitted}
				onClick={() => openFeedback(1)}
				size="small"
				sx={{
					color:
						feedbackSubmitted && feedbackRating === 1
							? "success.main"
							: "text.secondary",
				}}
				title="Helpful"
			>
				<ThumbUpAltOutlinedIcon fontSize="small" />
			</IconButton>
			<IconButton
				disabled={!canFeedback || feedbackSubmitted}
				onClick={() => openFeedback(-1)}
				size="small"
				sx={{
					color:
						feedbackSubmitted && feedbackRating === -1
							? "error.main"
							: "text.secondary",
				}}
				title="Not helpful"
			>
				<ThumbDownAltOutlinedIcon fontSize="small" />
			</IconButton>
		</Stack>
	);

	return (
		<Paper
			elevation={2}
			sx={{
				p: 3,
				borderRadius: 2,
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
			}}
		>
			<Stack spacing={3}>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: "bold" }}
				>
					AI-Powered Search
				</Typography>

				<TextField
					fullWidth
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={isLoading}
					variant="outlined"
					multiline
					sx={{
						"& .MuiOutlinedInput-root": {
							"borderRadius": 2,
							"backgroundColor": "background.default",
							"&:hover": {
								backgroundColor: "action.hover",
							},
							"&.Mui-focused": {
								backgroundColor: "background.paper",
							},
						},
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: "divider",
						},
					}}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment
									position="start"
									sx={{
										marginLeft: 2,
										color: "text.secondary",
									}}
								>
									<AutoAwesomeIcon
										color="primary"
										sx={{
											opacity: 0.8,
										}}
										titleAccess="AI-powered search"
									/>
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment
									position="end"
									sx={{
										marginRight: 1,
										gap: 0.5,
									}}
								>
									{!!inputValue && !isLoading && (
										<Tooltip title="Clear" arrow>
											<IconButton
												size="small"
												aria-label="Clear search"
												onClick={() => {
													setInputValue("");
												}}
												sx={{
													"borderRadius": 2,
													"padding": 0.75,
													"&:hover": {
														backgroundColor:
															"action.hover",
													},
												}}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
									<IconButton
										onClick={handleSearch}
										disabled={
											!inputValue.trim() || isLoading
										}
										size="small"
										sx={{
											"borderRadius": 2,
											"padding": 1,
											"&:hover": {
												backgroundColor: "primary.main",
												color: "primary.contrastText",
											},
										}}
									>
										<ArrowRightAltIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							),
						},
					}}
				/>

				{isLoading && (
					<Stack
						direction="row"
						spacing={2}
						alignItems="center"
						justifyContent="center"
						sx={{ py: 2 }}
					>
						<CircularProgress size={20} color="primary" />
						<Typography variant="body2" color="text.secondary">
							Thinking...
						</Typography>
					</Stack>
				)}					
				{response && (
					<Stack spacing={2.5}>
						{response.clarify ? (
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
							>
								<HelpIcon color="warning" fontSize="small" />
								<Typography
									variant="body1"
									color="text.secondary"
								>
									{`Could you please clarify what you're looking for?`}
								</Typography>
							</Stack>
						) : response.type === "network_error" ?(
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
							>
								<ErrorIcon color="error" fontSize="small" />
								<Typography
									variant="body1"
									color="text.secondary"
								>
									{`Chrome automatically blocks local network access by default which disables MCC chatbot in Chrome. Please go to the `} <Link href="https://gm.enterprise.slack.com/docs/T079PJWD3B9/F09UAFFMBPA" variant="inherit" color="blue" underline="hover">#ask-mcc</Link> {` SLACK channel for instructions on how to disable this setting.`}									
								</Typography>
							</Stack>
							
						): response.type === "table" &&
						  response.columns &&
						  response.rows ? (
							<Stack spacing={2}>
								<Typography variant="body1">
									{response.answer}
								</Typography>

								<TableContainer
									component={Paper}
									variant="outlined"
									sx={{
										borderRadius: 2,
									}}
								>
									<Table size="small">
										<TableHead>
											<TableRow>
												{response.columns.map(
													(column, index) => (
														<TableCell
															key={index}
															sx={{
																fontWeight:
																	"bold",
																backgroundColor:
																	"background.default",

																borderColor:
																	"divider",
																py: 1,
																px: 2,
																color: "text.primary",
															}}
														>
															{column}
														</TableCell>
													)
												)}
											</TableRow>
										</TableHead>
										<TableBody>
											{response.rows.map(
												(row, rowIndex) => (
													<TableRow
														key={rowIndex}
														hover
														sx={{
															"backgroundColor":
																"background.default",
															"&:hover": {
																backgroundColor:
																	"action.hover",
															},
															"&:last-child td": {
																borderBottom:
																	"none",
															},
														}}
													>
														{row.map(
															(
																cell,
																cellIndex
															) => (
																<TableCell
																	key={
																		cellIndex
																	}
																	sx={{
																		py: 1,
																		px: 2,
																	}}
																>
																	{String(
																		cell
																	)}
																</TableCell>
															)
														)}
													</TableRow>
												)
											)}
										</TableBody>
									</Table>
								</TableContainer>								
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									justifyContent="flex-start"
								>
									{feedbackActions}
								</Stack>	
							</Stack>
						)  : (
							<Stack spacing={1}>
								<Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
									{response.answer}
								</Typography>
								{response.action_url && isSafeActionUrl(response.action_url) && (
									<Link
										href={response.action_url}
										target="_blank"
										rel="noopener noreferrer"
										variant="body2"
										sx={{ fontWeight: 600 }}
										color="blue" 
										underline="hover"
										aria-label={response.action_label?.trim() || response.action_url}
									>
										{response.action_label?.trim() || response.action_url}
									</Link>
								)}
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									justifyContent="flex-start"
								>
									{feedbackActions}
								</Stack>
							</Stack>
						)}
					</Stack>
				)}

				<Dialog
					open={feedbackOpen}
					onClose={() =>
						!submittingFeedback && setFeedbackOpen(false)
					}
					fullWidth
					maxWidth="xs"
				>
					<DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
						{feedbackSubmitted
							? "Thank you"
							: feedbackRating === 1
								? "What was helpful?"
								: "How can we improve?"}
					</DialogTitle>
					<DialogContent sx={{ overflow: "visible" }}>
						{!feedbackSubmitted ? (
							<Stack spacing={2}>
								<TextField
									autoFocus
									label="Optional feedback"
									placeholder="Add more context (optional)"
									multiline
									minRows={3}
									value={feedbackComment}
									onChange={(e) =>
										setFeedbackComment(e.target.value)
									}
									fullWidth
									variant="outlined"
									InputLabelProps={{
										sx: {
											"px": 0.5,
											"backgroundColor": (theme) =>
												theme.palette.background.paper,
											"mr": 1,
											// keep label above outline clean
											"&.Mui-focused": {
												color: "primary.main",
											},
										},
									}}
									sx={{
										"& .MuiOutlinedInput-root": {
											alignItems: "flex-start",
										},
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									Your feedback helps improve future answers.
								</Typography>
							</Stack>
						) : (
							<Typography variant="body2" color="text.secondary">
								Received.
							</Typography>
						)}
					</DialogContent>
					<DialogActions sx={{ px: 3, pb: 2 }}>
						{!feedbackSubmitted && (
							<>
								<Button
									onClick={() => setFeedbackOpen(false)}
									disabled={submittingFeedback}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={handleSubmitFeedback}
									disabled={submittingFeedback}
								>
									{submittingFeedback
										? "Sending..."
										: "Submit"}
								</Button>
							</>
						)}
					</DialogActions>
				</Dialog>
			</Stack>
		</Paper>
	);
}
